import { ReactNode } from "react";
import { Component, ComponentConfig } from "./Component";
import { uuid } from "datasheets/model/utils/uuid";
import { WeakLRUCache } from "weak-lru-cache";

type ComponentConfigGenerator = () => ComponentConfig;
type ComponentGenerator = (config: Component) => ReactNode;
type EditorGenerator = (config: Component) => ReactNode;

interface ComponentDefinition {
  config: ComponentConfigGenerator;
  component: ComponentGenerator;
  editor: EditorGenerator;
  fixedOutputs?: () => string[];
  fixedInputs?: () => string[];
}
class FormComponentFactoryType {
  map: {
    [componentType: string]: ComponentDefinition;
  };
  aliases: { [oldView: string]: string };
  componentCache = new WeakLRUCache<string, ReactNode>();
  editorCache = new WeakLRUCache<string, ReactNode>();
  constructor() {
    this.map = {};
    this.aliases = {};
  }
  addComponent(viewType: string, definition: ComponentDefinition) {
    this.map[viewType] = definition;
  }
  addAlias(oldViewType: string, viewType: string) {
    this.aliases[oldViewType] = viewType;
  }
  _getGenerators(componentType: string): ComponentDefinition {
    if (componentType in this.map) {
      return this.map[componentType];
    } else if (componentType in this.aliases) {
      return this.map[this.aliases[componentType]];
    } else {
      console.debug(Object.keys(this.map));
      throw new Error(
        "Unregistered component: " +
          componentType +
          " not in " +
          Object.keys(this.map)
      );
    }
  }
  generateConfig(componentType: string): Component {
    const baseConfig = {
      id: uuid(),
      type: componentType,
      top: 0,
      left: 0,
    };
    const subConfig = this._getGenerators(componentType).config();
    return { ...baseConfig, ...{ config: subConfig, editConfig: subConfig } };
  }
  generateComponent(componentType: string, config: Component): ReactNode {
    let component = this.componentCache.getValue(config.id);
    if (!component) {
      component = this._getGenerators(componentType).component(config);
      this.componentCache.setValue(config.id, component);
    }
    return component;
  }
  generateEditor(componentType: string, config: Component): ReactNode {
    let component = this.editorCache.getValue(config.id);
    if (!component) {
      console.log("Generating new editor for ", config.id);
      component = this._getGenerators(componentType).editor(config);
      this.editorCache.setValue(config.id, component);
    }
    return component;
  }
  generateComponentOrEditor(
    componentType: string,
    config: Component,
    isEdit: boolean
  ): ReactNode {
    return isEdit
      ? this.generateEditor(componentType, config)
      : this.generateComponent(componentType, config);
  }
  getFixedOutputs(componentType: string) {
    if (componentType in this.map) {
      const componentDefinition = this.map[componentType];
      if (!!componentDefinition.fixedOutputs) {
        return componentDefinition.fixedOutputs();
      }
    }
    return [];
  }
  getFixedInputs(componentType: string) {
    if (componentType in this.map) {
      const componentDefinition = this.map[componentType];
      if (!!componentDefinition.fixedInputs) {
        return componentDefinition.fixedInputs();
      }
    }
    return [];
  }
  availableTypes(): string[] {
    return Object.keys(this.map);
  }
}

export const FormComponentFactory = new FormComponentFactoryType();
